import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faClose, faMinus } from '@fortawesome/sharp-solid-svg-icons';
import clsx from 'clsx';
import React from 'react';
import { NumericFormat } from 'react-number-format';
import { twMerge } from 'tailwind-merge';

import FormController from '@/components/form/formController';
import { INPUT_HEIGHT } from '@/constants/input';
import { IFormFieldProps, InputSize } from '@/models/form/form';
import { removeTrailingZeros } from '@/utils/form';
import { isDefined } from '@/utils/guards';

const BaseNumberInput = ({
  dataTestId = '',
  placeholder,
  value = '',
  onChange,
  name,
  onBlur,
  onFocus,
  className = undefined,
  endAdornment,
  additionalProps,
  disabled,
  showClearButton = false,
  size = InputSize.MEDIUM,
  startAdornment = undefined,
}: IFormFieldProps) => {
  const handleBlur = (e?: React.FocusEvent<HTMLInputElement, Element>) => {
    onFocus?.(false);
    onBlur?.();

    if (!e || !e.target.value) return;

    const cleanedValue = removeTrailingZeros(e.target.value);

    if (cleanedValue || cleanedValue === 0) {
      onChange({ target: { value: cleanedValue } });
    }
  };

  const handleIncrease = () => {
    const newValue = value ? value?.toString().replace(/,/g, '') : 0;
    onFocus?.(true);

    onChange({ target: { value: Number(newValue) + 1 } });
    handleBlur();
  };

  const handleDecrease = () => {
    const newValue = value ? value.toString().replace(/,/g, '') : 0;
    onFocus?.(true);

    if (newValue === 0) {
      onChange({ target: { value: 0 } });
    } else if (Number(newValue) < 1) {
      onChange({ target: { value: 0 } });
    } else if (Number(newValue) !== 0) {
      onChange({ target: { value: Number(newValue) - 1 } });
    }

    handleBlur();
  };

  const handleClearValue = () => {
    onChange({ target: { value: null } });
    onBlur?.();
  };

  return (
    <div
      className={twMerge(
        'flex h-10 w-full items-center space-x-3 bg-gray-10',
        className
      )}
      data-testid={dataTestId}
      style={{ height: INPUT_HEIGHT[size] }}
    >
      {startAdornment && <div className="ml-4">{startAdornment}</div>}
      <NumericFormat
        valueIsNumericString
        allowLeadingZeros={false}
        allowNegative={false}
        className={twMerge(
          'h-full w-full flex-grow border-none bg-transparent focus:outline-none focus:ring-0',
          startAdornment ? 'pl-0' : 'pl-4',
          size === InputSize.SMALL && 'pl-2'
        )}
        decimalScale={
          isDefined(additionalProps?.decimalScale)
            ? additionalProps?.decimalScale
            : 2
        }
        disabled={disabled}
        name={name}
        placeholder={placeholder}
        suffix={additionalProps?.suffix || ''}
        value={value !== undefined && value !== null ? String(value) : ''}
        onBlur={handleBlur}
        onChange={onChange}
        onFocus={() => onFocus?.(true)}
      />
      {endAdornment?.() && (
        <div
          className={clsx(
            'h-5 w-6 bg-transparent',
            !additionalProps?.showStepper && 'pr-4'
          )}
        >
          {endAdornment?.()}
        </div>
      )}
      {isDefined(value) && String(value).length > 0 && showClearButton && (
        <button
          className="!mr-3"
          data-testid="clear-button"
          type="button"
          onClick={handleClearValue}
        >
          <FontAwesomeIcon
            className="text-gray-70 hover:text-gray-100"
            icon={faClose}
            size="lg"
          />
        </button>
      )}
      {additionalProps?.showStepper && (
        <div className="flex divide-x">
          <button
            className="z-80 h-12 w-12 ring-blue-60 hover:bg-gray-20 focus:ring-2"
            data-testid="decrease-input-value"
            disabled={disabled}
            type="button"
            onClick={handleDecrease}
          >
            <FontAwesomeIcon
              className="text-gray-70 hover:text-gray-100"
              icon={faMinus}
            />
          </button>
          <button
            className="h-12 w-12 ring-blue-60 hover:bg-gray-20 focus:ring-2"
            data-testid="increase-input-value"
            disabled={disabled}
            type="button"
            onClick={handleIncrease}
          >
            <FontAwesomeIcon
              className="text-gray-70 hover:text-gray-100"
              icon={faAdd}
            />
          </button>
        </div>
      )}
    </div>
  );
};

const NumberInput = (props: IFormFieldProps) => {
  const { control } = props;

  if (control) {
    return <FormController {...props} FormField={BaseNumberInput} />;
  }

  return <BaseNumberInput {...props} />;
};

export default NumberInput;
